import {
  AllStructuresForHomePageQuery,
  PaintBlocksCorrodedArea,
  PointOfInterestsFindingsByDeckAndPaintBlock,
  UnitsEnum,
  UnitSystemEnum,
  useDefectsAreaAndPointOfInterestCountByPaintRegionsQuery,
} from '@/__generated__/graphql';
import { DefectLegend } from '@/components/Analysis/Insights/InsightsExplorer/PaintBlock/PaintBlockContainer';
import { PaintBlockGraph } from '@/components/Analysis/Insights/InsightsExplorer/PaintBlock/Summary';
import {
  useActiveCoatingConditionLegends,
  useAllCoatingConditions,
  useTemplateFieldNames,
} from '@/components/Analysis/Insights/InsightsExplorer/PaintBlock/Summary/hooks/useDefectsUtils';
import * as state from '@/components/Analysis/state';
import { RowData } from '@/components/shared/Table/DataTable';
import { XlsxProcessor } from '@/utils/xlsx';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { GridColDef } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { PLATFORM_EXPORT_CORROSION_GROUPKEY } from '@/constants';
import { getTotalCorrosionArea } from '../../../utils';

type Props = {
  activeDeploymentIds: string[];
  setPaintBlockTableXlsxProcessor: Dispatch<SetStateAction<XlsxProcessor | undefined>>;
  activeDeployments: AllStructuresForHomePageQuery['allStructures'] | undefined;
};

export function PaintBockAndDeckGraphsContainer({
  activeDeploymentIds,
  setPaintBlockTableXlsxProcessor,
  activeDeployments,
}: Props) {
  const [defectAreaForPaintBlocksAndDecks, setDefectAreaForPaintBlocksAndDecks] = useState<
    PaintBlocksCorrodedArea | undefined
  >(undefined);
  const [poiCountForPaintBlocksAndDecks, setPoiCountForPaintBlocksAndDecks] = useState<
    PointOfInterestsFindingsByDeckAndPaintBlock | undefined
  >(undefined);
  const [defectLegend, setDefectLegend] = useState<DefectLegend[] | undefined>([]);
  const [defectsForExport, setDefectForExport] = useState<string[] | undefined>([]);
  const [selectedConditionType, setSelectedConditionType] = useState<string>('');
  const [selectedCoatingCondition, setSelectedCoatingCondition] = useState<string[]>([]);
  const [selectedPOIType, setSelectedPOIType] = useState<string[]>([]);
  const unitSystem = useAtomValue(state.unitSystem);
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);

  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });

  const shouldRunQuery = activeDeploymentIds?.length > 0;
  const { loading: combinedLoading } = useDefectsAreaAndPointOfInterestCountByPaintRegionsQuery({
    variables: {
      input: {
        structureIds: activeDeploymentIds,
      },
    },
    skip: !shouldRunQuery,
    onCompleted: (data) => {
      // Handle Defects Area Data
      if (data?.defectsAreaForAssemblyPaintRegions) {
        setDefectAreaForPaintBlocksAndDecks(data.defectsAreaForAssemblyPaintRegions);
      } else {
        showSnackBar('Defects Area Data Not Found', 'error');
      }

      // Handle Point of Interest Data
      if (data?.pointOfInterestCountByPaintRegions) {
        setPoiCountForPaintBlocksAndDecks(data.pointOfInterestCountByPaintRegions);
      } else {
        showSnackBar('Point of Interest Data Not Found', 'error');
      }
    },
  });
  const mergedDefectLegends = useMemo(() => {
    if (!activeDeployments?.length) return [];

    return activeDeployments.reduce((acc: DefectLegend[], deployment) => {
      const defects = deployment.defectLegends || [];
      defects.forEach((defect) => {
        if (!acc.find((d) => d.name.toLowerCase() === defect.name.toLowerCase())) {
          acc.push(defect);
        }
      });
      return acc;
    }, []);
  }, [activeDeployments]);

  useEffect(() => {
    setDefectLegend(mergedDefectLegends);
    setDefectForExport(
      mergedDefectLegends
        .filter((defect) => defect.groupKey === PLATFORM_EXPORT_CORROSION_GROUPKEY)
        .map((defect) => defect.name.toLowerCase())
    );
    setSelectedConditionType(mergedDefectLegends[0]?.groupName ?? '');
  }, [mergedDefectLegends]);

  const templateFieldNames = useTemplateFieldNames(defectLegend, selectedConditionType);
  const allCoatingConditions = useAllCoatingConditions(
    defectLegend,
    selectedConditionType,
    templateFieldNames,
    setSelectedCoatingCondition
  );
  const activeCoatingConditionLegends = useActiveCoatingConditionLegends(
    defectLegend,
    templateFieldNames
  );

  const changePOITypeTab = useCallback((name: string) => {
    setSelectedPOIType((previousRows) =>
      previousRows.includes(name)
        ? previousRows.filter((rowName) => rowName !== name)
        : [...previousRows, name]
    );
  }, []);

  const changeCoatingTab = useCallback((name: string) => {
    setSelectedCoatingCondition((previousRows) =>
      previousRows.includes(name)
        ? previousRows.filter((rowName) => rowName !== name)
        : [...previousRows, name]
    );
  }, []);

  const template = useMemo(() => {
    const combinedData: Array<{ id: string; name: string }> = [];
    allPointOfInterestTemplates.forEach((item) => {
      if (item) combinedData.push({ id: item.id, name: item.name });
    });
    defectLegend?.forEach((item) => {
      combinedData.push({ id: item.id.toString(), name: item.name });
    });
    return combinedData;
  }, [allPointOfInterestTemplates, defectLegend]);

  const rows: RowData[] = useMemo(() => {
    return (
      [...(defectAreaForPaintBlocksAndDecks?.defectsByPaintBlock ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        ?.map((paintRegionData) => {
          const paintRegion = { ...paintRegionData };
          const corrodedArea = defectAreaForPaintBlocksAndDecks?.defectsByPaintBlock.find(
            (area) => area.name === paintRegion.name
          );

          const totalArea = localiseMeasurement({
            unitSystem,
            sourceUnit: UnitsEnum.SquareMeter,
            value: getTotalCorrosionArea(defectsForExport, corrodedArea),
            showUnitLabel: false,
          });

          const corrodedAreaTypes: { [key: string]: string | number } = {};
          corrodedArea?.totalArea
            ?.filter((areaType) => defectsForExport?.includes(areaType?.type.toLowerCase() ?? ''))
            ?.forEach((areaType) => {
              if (areaType?.type && areaType?.value) {
                corrodedAreaTypes[areaType.type.toLowerCase()] =
                  localiseMeasurement({
                    unitSystem,
                    sourceUnit: UnitsEnum.SquareMeter,
                    value: areaType.value,
                    showUnitLabel: false,
                  }) ?? 0;
              }
            });
          const poiFindingByPaintBlock = poiCountForPaintBlocksAndDecks?.findingsByPaintBlock?.find(
            ({ name }) => name === paintRegion.name
          );
          const paintRegionIds = [...(paintRegion.paintRegionIds ?? [])];
          const rowData: RowData = {
            id: paintRegionIds && paintRegionIds.length > 0 ? paintRegionIds?.pop() ?? '' : '',
            block: paintRegion.name,
            corrodedArea: totalArea ?? 0,
            findings: poiFindingByPaintBlock?.totalCount ?? 0,
            ...corrodedAreaTypes,
          };

          poiFindingByPaintBlock?.pointOfInterestFindings?.forEach((fieldData) => {
            if (fieldData?.type && fieldData?.count) {
              rowData[fieldData.type.toLowerCase()] = fieldData.count;
            }
          });

          return rowData;
        }) ?? []
    );
  }, [
    defectAreaForPaintBlocksAndDecks?.defectsByPaintBlock,
    unitSystem,
    poiCountForPaintBlocksAndDecks?.findingsByPaintBlock,
    defectsForExport,
  ]);

  const columns = useMemo(() => {
    const fieldColumns: GridColDef[] = [
      {
        field: 'block',
        headerName: 'BLOCK#',
        type: 'string',
        width: 100,
      },
      {
        field: 'findings',
        headerName: 'TOTAL FINDINGS',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 100,
        flex: 1,
      },
    ];
    template?.forEach((field) => {
      if (defectsForExport?.includes(field?.name.toLowerCase())) {
        const hasUnits = !Object.values(allPointOfInterestTemplates)
          .map((poiTemplate) => poiTemplate?.name.toUpperCase())
          .includes(field?.name?.toUpperCase());
        const units = unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)';
        fieldColumns.push({
          field: field?.name.toLowerCase(),
          headerName: `${field.name.toUpperCase()} ${hasUnits ? units : ''}`,
          type: 'number',
          width: field.name.length * 10,
          flex: 1,
        });
      }
    });

    return fieldColumns;
  }, [allPointOfInterestTemplates, defectsForExport, template, unitSystem]);

  const cols = useMemo(
    () =>
      columns
        .filter((column) => !column.hideable)
        .map((col) => ({
          label: col.headerName ?? 'no label',
          data: rows?.map((row) => row[col.field] || ''),
        })),
    [columns, rows]
  );

  const xlsx = useMemo(
    () =>
      new XlsxProcessor({
        columns: cols,
        sheetName: 'Aggregate Ri Values',
      }),
    [cols]
  );

  useEffect(() => {
    setPaintBlockTableXlsxProcessor(xlsx);
  }, [xlsx, setPaintBlockTableXlsxProcessor]);
  return (
    <PaintBlockGraph
      findingsByPaintBlocksAndDecks={poiCountForPaintBlocksAndDecks}
      defectAreaForPaintBlocksAndDecks={defectAreaForPaintBlocksAndDecks}
      selectedPOIType={selectedPOIType}
      changePOITypeTab={changePOITypeTab}
      changeCoatingTab={changeCoatingTab}
      allCoatingConditions={allCoatingConditions}
      selectedCoatingCondition={selectedCoatingCondition}
      selectedConditionType={selectedConditionType}
      setSelectedConditionType={setSelectedConditionType}
      graphsLoading={combinedLoading}
      coatingConditionLegend={activeCoatingConditionLegends}
      defects={defectLegend}
      activeStructureIds={activeDeploymentIds}
    />
  );
}
